import { Button } from '@hourly-hds/components';
import { Delete, Download } from '@hourly-hds/icons/glyph';
import { ChangeEvent, useRef, useState } from 'react';
import TableEmptyState from '@shared/components/TableEmptyState/TableEmptyState';
import { TabContentSpinner } from '../../../_shared/components/TabContentSpinner';
import DeleteDocumentDialog from './components/DeleteDocumentDialog';
import File from './components/File';
import { useDocuments } from './data/hooks/useDocuments';
import styles from './Documents.module.css';

export default function Documents({ partnerId }: { partnerId: string }) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { documents, otherDocuments, downloadDocument, uploadDocument, deleteDocument, isSettled } =
    useDocuments(partnerId);

  if (!isSettled) {
    return <TabContentSpinner />;
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadDocument({ partnerId, file });
      event.target.value = '';
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedDocumentId(null);
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDeleteDialog = async () => {
    await deleteDocument({ partnerId, documentId: selectedDocumentId! });
    setSelectedDocumentId(null);
    setIsDeleteDialogOpen(false);
  };

  return (
    <div className={styles.documentsWrap}>
      <div className={styles.header}>
        <p className={styles.title}>Documents</p>
      </div>
      <div className={styles.content}>
        {documents.length === 0 ? (
          <TableEmptyState />
        ) : (
          documents.map((doc) => {
            return (
              <div className={styles.fileWrapper} key={doc.id}>
                <File
                  data={{ ...doc, label: doc.type, size: '' }}
                  primaryAction={
                    <Download
                      aria-label={`download: ${doc.name}`}
                      onClick={async () => {
                        const url = await downloadDocument({ partnerId, documentId: doc.id });
                        window.open(url, '__blank');
                      }}
                    />
                  }
                />
              </div>
            );
          })
        )}
      </div>

      <div className={`${styles.documentsWrap} ${styles.otherDocumentsWrap}`}>
        <div className={styles.header}>
          <p className={styles.title}>Other Documents</p>
          <Button label="Upload" onClick={handleUploadClick} />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.csv,.xls,.xlsx"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-label="Upload file for other documents"
        />

        <div className={styles.content}>
          {otherDocuments.length === 0 ? (
            <TableEmptyState />
          ) : (
            otherDocuments.map((doc) => {
              return (
                <div className={styles.fileWrapper} key={doc.id}>
                  <File
                    data={{ ...doc, label: 'Document', size: '' }}
                    primaryAction={
                      <Download
                        aria-label={`download: ${doc.name}`}
                        onClick={async () => {
                          const url = await downloadDocument({ partnerId, documentId: doc.id });
                          window.open(url, '__blank');
                        }}
                      />
                    }
                    secondaryAction={
                      <Delete
                        aria-label={`delete: ${doc.name}`}
                        onClick={() => handleDeleteDocument(doc.id)}
                      />
                    }
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
      <DeleteDocumentDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteDialog}
      />
    </div>
  );
}
